import ContentWrapper from "src/components/contentwrapper/ContentWrapper";
import FilmwebLink from "src/components/filmweblink/FilmwebLink";
import PåskekalenderSeo from "src/components/seo/PåskekalenderSeo";
import theme from "src/components/themes/DefaultTheme";
import { endOfToday } from "date-fns";
import { setDefaultCacheHeaders } from "src/lib/server/cache";
import { getServerData, sanityQuery } from "src/lib/server/sanity";
import styled, { createGlobalStyle, css } from "src/lib/styles/css";
import IFilmnytt from "src/lib/types/Filmnytt";
import IKonkurranseKalender from "src/lib/types/KonkurranseKalender";
import KonkurranseKalenderProps from "src/lib/types/konkurransekalender/KonkurranseKalenderProps";
import { getUrlForDoctype } from "src/lib/types/sanity";
import { GetServerSideProps } from "next";
import { groq } from "next-sanity";

//#region [Other]getServerSideProps
export const getServerSideProps: GetServerSideProps = getServerData(async (context) => {
    setDefaultCacheHeaders(context);

    const cal = await sanityQuery<IKonkurranseKalender>(CAL_QUERY);

    let luker: (IFilmnytt | null)[] = cal?.luker?.slice(0, 3) ?? [];
    if (luker.length < 3) {
        luker = Array.from({ ...luker, length: 3 }).map(l => !l ? null : l);
    }
    return {
        props: {
            luker
        } as KonkurranseKalenderProps
    };

});

const CAL_QUERY = groq`*[_id == "outlandeaster2024"] {
	...,
	luker[]->
}[0]`;
//#endregion

//#region [Component]
export default function PaaskeKalender({ appData, luker }: KonkurranseKalenderProps) {

    return <section>
        <CalBG />

        <PåskekalenderSeo />

        <SContentWrapper>
            <HeaderImage />
            <SDragonContainer>
                <SDragonImage src="/paaskekalender2024/DrageDesktop.png" alt="Dragen Outly passer på eggene!" width={2072} height={2254} />
                {luker.map((l, index) => <Egg key={l?._id ?? index} eggIndex={index} quiz={l ?? undefined} />)}
            </SDragonContainer>
            <PrizesImage />
            <SHelperText>Klikk på et egg for å komme til påskequizen</SHelperText>
        </SContentWrapper>

    </section>;
}
//#endregion

//#region [Component]
const HeaderImage = () => {
    return <SHeaderImage>
        <source srcSet="/paaskekalender2024/FWsPaaskeQuizDesktopTT.png" media={theme.mq.desktop} />
        {
            // eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
        }<img alt="Filmwebs påskequiz i samarbeid med Outland" src="/paaskekalender2024/FWsPaaskeQuizTTMobil.png"/>
    </SHeaderImage>
}
//#endregion

//#region [Component]
const PrizesImage = () => {
    return <SPrizes>
        <source srcSet="/paaskekalender2024/PremiebildeDesktop.png" media={theme.mq.desktop} />
        {
            // eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
        }<img src="/paaskekalender2024/PremiebildeMobil.png" alt="Premiene i påskequiz'en"/>
    </SPrizes>
}
//#endregion

type EggProps = {
    eggIndex: number;
    quiz?: IFilmnytt;
}

const eggCss = [
    {
        top: "24.9%",
        left: "26.6%",
        width: "5%"
    },
    {
        top: "17%",
        left: "38%",
        width: "5%"
    },
    {
        top: "27%",
        left: "43%",
        width: "8%"
    }
]

//#region [Component]
const Egg = ({ eggIndex, quiz }: EggProps) => {
    // eslint-disable-next-line @next/next/no-img-element
    const EggImg = <img src={`/paaskekalender2024/Nummer${eggIndex + 1}${quiz ? "" : "IkkeAktiv"}.png`} alt={`Åpne drageegg ${eggIndex + 1}`} />;

    if (quiz) {
        const url = getUrlForDoctype(quiz._type, quiz.slug.current, quiz.isStreaming as boolean, quiz);
        return <SEgg1Link to={url!} style={eggCss[eggIndex]}>
            {EggImg}
        </SEgg1Link>
    }
    return <SEgg1 style={eggCss[eggIndex]}>
        {EggImg}
    </SEgg1>

}
//#endregion

const SEgg1CSS = css`
    position: absolute;
    z-index: 3;
`;

const SEgg1Link = styled(FilmwebLink)`
    ${SEgg1CSS}
`;

const SEgg1 = styled.div`
    ${SEgg1CSS}
`;

//#region [Styles]
const CalBG = createGlobalStyle`
	body {
		background-image: url("/paaskekalender2024/BGMobil.jpg");
		background-repeat: no-repeat;
		background-position: center 55px;
		background-size: cover;

         @media ${theme.mq.desktop} {
            background-image: url("/paaskekalender2024/BG.jpg");
            background-size: contain;
         }
	}
`;

const SContentWrapper = styled(ContentWrapper)`
    min-height: calc(100vh - 55px);
	@media screen and (min-width: 1205px) {
		margin-bottom: 150px;

	}
`;

const SHeaderImage = styled.picture`
    width: 100%;
    display: block;
    padding: 20px 20px 0 20px;

    @media ${theme.mq.desktop} {
        width: 50%;
    }
`;

const SDragonContainer = styled.div`
    margin-top: -3%;
    position: relative;
    overflow: hidden;
    width: 100vw;
    z-index: 1;

    @media ${theme.mq.desktop} {
        margin-top: -20%;
        width: 50%;
        margin-left: 39%;
    }
`;

const SDragonImage = styled.img`
    max-width: none;
    display: block;
    position: relative;
    z-index: 2;
    width: calc(100vw + 50px);
    transform: translateX(-25px);
    @media ${theme.mq.desktop} {
        transform: none;
        width: 100%;
    }
`;

const SPrizes = styled.picture`
    display: block;
    position: relative;
    z-index: 2;
    width: 98%;
    margin: -60% 1% 0 1%;

    @media ${theme.mq.desktop} {
        margin: 0;
        width: 35%;
        margin: -25% 1% 0 15%;
    }
`;

const SHelperText = styled.p`
    text-align: center;
    @media ${theme.mq.desktop} {
        margin-top: 60px;
    }
`;
//#endregion
